/**
 * Conversation Service
 * Handles all conversation and message-related API operations
 */

import { filenameFrom, triggerBlobDownload } from '@/utils/download';
import api from '@services/api';
import { createLogger } from '@/utils/logger';
import { answeredByOf } from '@/utils/modelLabels';
import { browserTimeZone } from '@/utils/browserTimeZone';
import { fileRefusalFromBody } from './fileValidationService';
import type { Conversation, Message, SendMessagePayload, SearchResult, StreamEvent, BackendMessage, BranchResult, AgentBranchResult, DriveFileRef, ContextUsage, CompactionInfo } from '@/types/models';
import { parseAgentHandoff } from '../utils/agentHandoff';

const logger = createLogger('ConversationService');

/** `POST /conversations/<id>/compact` — see `compactNow`. */
export interface CompactNowResult {
  compacted: boolean;
  reason?: 'NOTHING_TO_COMPACT' | 'COMPACTION_FAILED' | string;
  error?: string;
  usage: ContextUsage;
  summary_message?: BackendMessage;
  summarized_count?: number;
  round?: number;
  tokens_before?: number;
  tokens_after?: number;
  folded_message_ids?: string[];
}

/**
 * The compaction marker a backend message row is, if it is one. Flattened
 * here because `Message` carries no raw metadata; `superseded` comes from
 * `compacted_into` (a later round folded this marker too).
 */
export function compactionInfoOf(msg: {
  sender_type?: string;
  message_metadata?: Record<string, any> | null;
  compacted_into?: string | null;
}): CompactionInfo | undefined {
  const meta = msg.message_metadata || {};
  if (msg.sender_type !== 'system' || !meta.compaction) return undefined;
  return {
    summarized_count: Number(meta.summarized_count || 0),
    round: Number(meta.round || 1),
    tokens_before: typeof meta.tokens_before === 'number' ? meta.tokens_before : undefined,
    tokens_after: typeof meta.tokens_after === 'number' ? meta.tokens_after : undefined,
    superseded: Boolean(msg.compacted_into),
  };
}

/**
 * A message as the transcript route serializes it (`Message.to_dict`) →
 * the chat's `Message`. The one mapping: the chat and Insights' read-only
 * conversation viewer both render through it, so they cannot drift.
 */
export function messageFromApi(msg: any): Message {
  return {
    message_id: msg.message_id,
    conversation_id: msg.conversation_id,
    role: msg.sender_type, // Backend uses sender_type, frontend uses role
    content: msg.message_content, // Backend uses message_content, frontend uses content
    model_name: msg.message_metadata?.model,
    answered_by: answeredByOf(msg),
    served_by_fallback: msg.message_metadata?.served_by_fallback === true,
    mode_label: msg.message_metadata?.mode_label,
    active_skills: msg.message_metadata?.active_skills || undefined,
    // Persisted "what tools/skills fired this turn" steps. Surfaced
    // in the AoA simulation panel (always) and in the regular chat
    // when the bot has reveal_thinking on. Backend persists this on
    // assistant message_metadata as a list of
    // {type, step_type, label, detail} objects.
    thinking_steps: msg.message_metadata?.thinking_steps || undefined,
    // Turns the two agent-handoff marker messages into rendered link cards
    // rather than raw "[Agent handoff] …" system text.
    agent_handoff: parseAgentHandoff(msg.message_metadata),
    // Flattened so the stop-reconcile (frontend/src/utils/stopReconcile.ts)
    // can tell a turn the user really cut short — written by
    // ConversationService._persist_interrupted_turn — from one that had
    // already finished when Stop was pressed. `Message` carries no raw
    // metadata, so the flag has to come across here.
    interrupted: msg.message_metadata?.interrupted === true,
    answer_cut_off: msg.message_metadata?.answer_cut_off === true,
    answer_cut_off_reason: msg.message_metadata?.answer_cut_off_reason,
    answer_limit_reached: msg.message_metadata?.answer_limit_reached === true,
    web_sources: msg.message_metadata?.web_sources,
    personal_connectors_used: msg.message_metadata?.personal_connectors_used,
    // Who wrote this. Null on assistant turns and on messages predating
    // shared projects. In a project the whole thread is co-authored, so the
    // author has to travel with the message or the transcript is unreadable.
    sender_user_id: msg.sender_user_id || undefined,
    sender_name: msg.sender_name || undefined,
    sender_email: msg.sender_email || undefined,
    sender_tenant_name: msg.sender_tenant_name || undefined,
    created_at: msg.created_at,
    attachments: msg.attachments || [],
    compaction: compactionInfoOf(msg),
  };
}

/**
 * Custom API Error with type information
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public type: 'auth' | 'validation' | 'network' | 'server' | 'unknown',
    /** Machine-readable error code from the backend body, when it sent one. */
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Turn a failed stream response into an ApiError carrying the message the
 * BACKEND wrote, not the raw envelope.
 *
 * These errors are surfaced straight to the user by ConversationPanel's send
 * handler, so dumping `HTTP 400: {"error":"no_available_models","message":…}`
 * put a JSON blob on screen where a sentence belongs — most visibly when an
 * admin switches off the only model family an agent is allowed to use.
 */
async function streamHttpError(response: Response, logLabel: string): Promise<ApiError> {
  return apiErrorFromBody(response, await response.text(), logLabel);
}

export function apiErrorFromBody(response: Response, errorText: string, logLabel: string): ApiError {
  console.error(`[conversationService] ${logLabel}:`, response.status, errorText);

  let errorType: ApiError['type'] = 'server';
  if (response.status === 401 || response.status === 403) errorType = 'auth';
  else if (response.status === 400) errorType = 'validation';
  else if (response.status === 0) errorType = 'network';

  let message = '';
  let code: string | undefined;
  try {
    const body = JSON.parse(errorText);
    if (typeof body?.message === 'string' && body.message.trim()) message = body.message.trim();
    else if (typeof body?.error === 'string' && body.error.trim()) message = body.error.trim();
    if (typeof body?.error_code === 'string') code = body.error_code;
    else if (typeof body?.error === 'string') code = body.error;
  } catch {
    // Not JSON — fall through to the raw text.
  }
  if (!message) message = errorText ? `HTTP ${response.status}: ${errorText}` : `HTTP ${response.status}`;

  return new ApiError(message, response.status, errorType, code);
}

export type ConversationExportFormat = 'pdf' | 'docx' | 'txt' | 'md';

class ConversationService {
  /**
   * Download the WHOLE conversation as Word, PDF or Markdown. The document's
   * own words follow `lang` and its times the browser's time zone; the file
   * is saved under the name the server gives it (the title, Hebrew kept).
   * `scope: 'meta'` reads a meta-agent session through its own route.
   */
  async exportConversation(
    conversationId: string,
    format: ConversationExportFormat,
    { lang, scope = 'chat' }: { lang: string; scope?: 'chat' | 'meta' },
  ): Promise<void> {
    const tz = browserTimeZone() ?? 'UTC';
    const base = scope === 'meta' ? '/agent-of-agents/conversations' : '/conversations';
    const res = await api.get(`${base}/${conversationId}/export`, {
      params: { format, lang, tz },
      responseType: 'blob',
    });
    triggerBlobDownload(res.data, filenameFrom(res.headers, `conversation.${format}`));
  }

  /**
   * Get conversations for current user with pagination
   */
  async getConversations(
    page: number = 1,
    perPage: number = 35,
    chatbotId?: string | null
  ): Promise<{
    conversations: Conversation[];
    pagination: {
      page: number;
      per_page: number;
      total: number;
      pages: number;
      has_next: boolean;
      has_prev: boolean;
    };
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    if (chatbotId !== undefined && chatbotId !== null) {
      params.append('chatbot_id', chatbotId);
    }
    const { data } = await api.get(`/conversations?${params}`);
    return {
      conversations: data.conversations || [],
      pagination: data.pagination,
    };
  }

  /**
   * Get distinct agents the user has conversed with, ordered by most recent activity.
   */
  async getConversationAgents(
    page: number = 1,
    perPage: number = 15
  ): Promise<{
    agents: Array<{
      chatbot_id: string | null;
      chatbot_name: string | null;
      last_activity: string | null;
      conversation_count: number;
    }>;
    pagination: {
      page: number;
      per_page: number;
      total: number;
      pages: number;
      has_next: boolean;
      has_prev: boolean;
    };
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    const { data } = await api.get(`/conversations/agents?${params}`);
    return {
      agents: data.agents || [],
      pagination: data.pagination,
    };
  }

  /**
   * Get a specific conversation by ID
   */
  async getConversation(id: string): Promise<Conversation> {
    const { data } = await api.get(`/conversations/${id}`);
    return data.conversation;
  }

  /**
   * Explicitly stop the in-flight turn server-side.
   *
   * Turns are detached from the connection (they survive navigation and
   * persist their result), so aborting the fetch alone no longer stops the
   * model - this endpoint sets the server-side cancellation token. Fire and
   * forget; safe when nothing is running.
   */
  /**
   * Stop the running turn. `shownChars` is how much of the answer is on the
   * screen at this moment (code points, as the server counts): the stopped
   * turn is stored with no more than that, although text already in flight
   * keeps reaching the server's socket after the click.
   */
  async stopGeneration(conversationId: string, shownChars?: number): Promise<void> {
    try {
      await api.post(`/conversations/${conversationId}/stop`,
        shownChars === undefined ? undefined : { shown_chars: shownChars });
    } catch {
      // Best-effort: an unreachable stop must not break the local stop UX.
    }
  }

  /**
   * Get messages for a conversation
   * Fetches all messages (no pagination) to ensure complete conversation history
   */
  async getMessages(conversationId: string): Promise<Message[]> {
    // Request all messages without pagination
    const { data } = await api.get(`/conversations/${conversationId}/messages?all=true`);
    const messages = data.messages || [];

    // Transform backend format to frontend format
    return messages.map(messageFromApi);
  }

  /**
   * How full the conversation is against `model`'s window (or the model it
   * last used). Read once on load and again when the picker changes — the
   * stream's `context_usage` events keep it current in between.
   */
  async getContextUsage(conversationId: string, model?: string): Promise<ContextUsage> {
    const { data } = await api.get(`/conversations/${conversationId}/context-usage`, {
      params: model ? { model } : undefined,
    });
    return data.usage as ContextUsage;
  }

  /**
   * "Compact now": fold the older part of the conversation into a summary
   * and keep the recent turns verbatim. Resolves with the marker to insert
   * and the ids of the rows now behind it; `compacted: false` when there was
   * nothing to fold (the server answers 409 for that — not an error).
   */
  async compactNow(conversationId: string, model?: string): Promise<CompactNowResult> {
    try {
      const { data } = await api.post(`/conversations/${conversationId}/compact`, model ? { model } : {});
      return data as CompactNowResult;
    } catch (error: any) {
      const payload = error?.response?.data;
      if (payload && payload.reason === 'NOTHING_TO_COMPACT') return payload as CompactNowResult;
      throw error;
    }
  }

  /**
   * Create a new conversation.
   *
   * `projectId` attaches the thread to a shared project: the backend validates
   * membership, stamps the PROJECT's tenant on the row, and from then on every
   * project member can read and continue it.
   */
  async createConversation(
    botId?: string,
    projectId?: string,
    threadMode?: 'group' | 'solo',
  ): Promise<Conversation> {
    const { data } = await api.post('/conversations', {
      chatbot_id: botId,
      ...(projectId ? { project_id: projectId } : {}),
      // Only for a project thread, and only when it is not the default:
      // outside a project there is nobody to exclude, so a mode is noise there.
      ...(projectId && threadMode === 'solo' ? { thread_mode: 'solo' } : {}),
    });
    return data.conversation;
  }

  /**
   * Send a message in a conversation
   */
  async sendMessage(
    conversationId: string,
    payload: SendMessagePayload
  ): Promise<Message> {
    // Files go through sendMessageWithFiles (/messages/with-files).
    const requestData = {
      message: payload.content,  // Backend expects 'message' not 'content'
      model_name: payload.model_name,
      generate_response: true,
    };

    const { data } = await api.post(
      `/conversations/${conversationId}/messages`,
      requestData
    );

    logger.debug('Backend response:', data);

    // Check if response has success and user_message
    if (!data || !data.success) {
      console.error('[conversationService] Invalid response format:', data);
      throw new Error(data?.error || 'Invalid response from server');
    }

    // Backend returns 'user_message' for the user's message
    const msg = data.user_message;

    if (!msg) {
      console.error('[conversationService] No user_message in response:', data);
      throw new Error('No message data in response');
    }

    // Transform backend message format to frontend format
    return {
      message_id: msg.message_id,
      conversation_id: msg.conversation_id,
      role: msg.sender_type,
      content: msg.message_content,
      model_name: msg.message_metadata?.model,
      answered_by: answeredByOf(msg),
      served_by_fallback: msg.message_metadata?.served_by_fallback === true,
      mode_label: msg.message_metadata?.mode_label,
      created_at: msg.created_at,
      attachments: msg.attachments || []
    };
  }

  /**
   * Create a branch conversation from a message point
   */
  async createBranch(conversationId: string, branchPointMessageId: string): Promise<BranchResult> {
    const { data } = await api.post(`/conversations/${conversationId}/branch`, {
      branch_point_message_id: branchPointMessageId
    });

    // Transform messages to frontend format
    const messages = (data.messages || []).map((msg: any) => ({
      message_id: msg.message_id,
      conversation_id: msg.conversation_id,
      role: msg.sender_type,
      content: msg.message_content,
      model_name: msg.message_metadata?.model,
      answered_by: answeredByOf(msg),
      served_by_fallback: msg.message_metadata?.served_by_fallback === true,
      mode_label: msg.message_metadata?.mode_label,
      active_skills: msg.message_metadata?.active_skills || undefined,
      created_at: msg.created_at,
      attachments: msg.attachments || []
    }));

    return {
      conversation: data.conversation,
      messages,
      branch_point_message_id: data.branch_point_message_id,
      parent_conversation_id: data.parent_conversation_id
    };
  }

  /**
   * Hand a conversation over to a DIFFERENT agent.
   *
   * Copies history up to `branchPointMessageId` into a new conversation bound
   * to `targetChatbotId`, and links the two threads in both directions: the
   * returned `messages` end with the new thread's inbound card, and
   * `origin_marker` is the outbound card the backend appended to the thread we
   * came from.
   */
  async branchToAgent(
    conversationId: string,
    branchPointMessageId: string,
    targetChatbotId: string
  ): Promise<AgentBranchResult> {
    const { data } = await api.post(`/conversations/${conversationId}/branch-to-agent`, {
      branch_point_message_id: branchPointMessageId,
      target_chatbot_id: targetChatbotId,
    });

    const toMessage = (msg: any): Message => ({
      message_id: msg.message_id,
      conversation_id: msg.conversation_id,
      role: msg.sender_type,
      content: msg.message_content,
      model_name: msg.message_metadata?.model,
      answered_by: answeredByOf(msg),
      served_by_fallback: msg.message_metadata?.served_by_fallback === true,
      mode_label: msg.message_metadata?.mode_label,
      active_skills: msg.message_metadata?.active_skills || undefined,
      agent_handoff: parseAgentHandoff(msg.message_metadata),
      created_at: msg.created_at,
      attachments: msg.attachments || [],
    });

    return {
      conversation: data.conversation,
      messages: (data.messages || []).map(toMessage),
      branch_point_message_id: data.branch_point_message_id,
      parent_conversation_id: data.parent_conversation_id,
      origin_marker: data.origin_marker ? toMessage(data.origin_marker) : undefined,
    };
  }

  /**
   * Get all branches of a conversation
   */
  async getBranches(conversationId: string): Promise<Conversation[]> {
    const { data } = await api.get(`/conversations/${conversationId}/branches`);
    return data.branches || [];
  }

  /**
   * Search conversations
   */
  async searchConversations(query: string): Promise<SearchResult[]> {
    const { data } = await api.post('/conversations/search', { query });
    return data.results || [];
  }

  /**
   * Delete a conversation
   */
  async deleteConversation(id: string): Promise<void> {
    await api.delete(`/conversations/${id}`);
  }

  /**
   * Update conversation title
   */
  async updateConversationTitle(id: string, title: string): Promise<Conversation> {
    const { data } = await api.put(`/conversations/${id}/title`, {
      title: title,
    });
    return data.conversation;
  }

  /**
   * Stream a message with AI response using async generator
   * Yields SSE events as they arrive
   */
  async *streamMessage(
    conversationId: string,
    payload: {
      content: string;
      provider?: string;
      model?: string;
      chatbot_id?: string;
      web_search?: boolean;
      edit_message_id?: string;
    }
  ): AsyncGenerator<StreamEvent, void, undefined> {
    const apiUrl = api.defaults.baseURL || (() => {
      throw new Error('API baseURL not configured. Check config service initialization.');
    })();
    const token = sessionStorage.getItem('access_token');
    const tenantId = sessionStorage.getItem('tenant_id');

    logger.debug('Starting stream:', {
      conversationId,
      contentLength: payload.content.length,
      provider: payload.provider,
      model: payload.model,
      editMessageId: payload.edit_message_id
    });

    const response = await fetch(
      `${apiUrl}/conversations/${conversationId}/stream`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-ID': tenantId || ''
        },
        body: JSON.stringify({
          message: payload.content,
          provider: payload.provider,
          model: payload.model,
          chatbot_id: payload.chatbot_id,
          web_search: payload.web_search || false,
          edit_message_id: payload.edit_message_id
        })
      }
    );

    if (!response.ok) {
      throw await streamHttpError(response, 'Stream error');
    }

    yield* this.readTurnEvents(response);
  }

  /**
   * Continue an answer the output cap paused (the latest message, with
   * `answer_cut_off`): what the model writes streams as `ai_chunk`s that
   * belong to THAT message, and `ai_complete` carries it, grown. No user
   * message is written. Same events and same Stop as `streamMessage`.
   */
  async *continueMessage(
    conversationId: string,
    messageId: string,
  ): AsyncGenerator<StreamEvent, void, undefined> {
    const apiUrl = api.defaults.baseURL || (() => {
      throw new Error('API baseURL not configured. Check config service initialization.');
    })();
    const response = await fetch(
      `${apiUrl}/conversations/${conversationId}/messages/${messageId}/continue`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`,
          'X-Tenant-ID': sessionStorage.getItem('tenant_id') || ''
        },
        body: '{}',
      }
    );
    if (!response.ok) {
      throw await streamHttpError(response, 'Continue error');
    }
    yield* this.readTurnEvents(response);
  }

  /** The SSE events of one streamed turn, until `done`. */
  private async *readTurnEvents(response: Response): AsyncGenerator<StreamEvent, void, undefined> {
    if (!response.body) {
      throw new Error('No response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    try {
      // Carry-over buffer: a `data:` line can span two reader.read() chunks
      // (guaranteed for large payloads like artifact_progress drafts).
      // Without it, the partial line fails JSON.parse and the event is
      // silently dropped. `: keepalive` comment lines fall out of the
      // `data: ` filter below.
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          logger.debug('Stream complete');
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? ''; // keep the trailing partial line

        for (const rawLine of lines) {
          const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
          if (!line.startsWith('data: ')) continue;

          try {
            const data = JSON.parse(line.slice(6));
            logger.debug('Stream event:', data.type);
            yield data as StreamEvent;

            // Exit only on done (the final event after ai_complete)
            if (data.type === 'done') {
              return;
            }
          } catch (error) {
            console.warn('[conversationService] Failed to parse SSE line:', line);
          }
        }
      }
    } catch (error) {
      console.error('[conversationService] Stream read error:', error);
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Network error. Please check your connection.');
      }
      throw error;
    } finally {
      try { reader.cancel(); } catch (_) { /* already closed */ }
    }
  }

  /**
   * Stream message with file attachments
   * Yields SSE events as files are processed and AI responds
   * Supports upload progress callback via XHR
   */
  async *streamMessageWithFiles(
    conversationId: string,
    payload: {
      content: string;
      files: File[];
      /** Google Drive picks — sent as ids; the backend fetches the bytes. */
      drive_files?: DriveFileRef[];
      provider?: string;
      model?: string;
      chatbot_id?: string;
      web_search?: boolean;
      edit_message_id?: string;
    },
    options?: {
      onUploadProgress?: (percent: number) => void;
      abortSignal?: AbortSignal;
    }
  ): AsyncGenerator<StreamEvent, void, undefined> {
    const apiUrl = api.defaults.baseURL || (() => {
      throw new Error('API baseURL not configured. Check config service initialization.');
    })();
    const token = sessionStorage.getItem('access_token');
    const tenantId = sessionStorage.getItem('tenant_id');

    logger.debug('Starting stream with files:', {
      conversationId,
      fileCount: payload.files.length,
      contentLength: payload.content.length,
      provider: payload.provider,
      model: payload.model,
      editMessageId: payload.edit_message_id
    });

    const formData = new FormData();
    formData.append('message', payload.content);
    if (payload.provider) formData.append('provider', payload.provider);
    if (payload.model) formData.append('model', payload.model);
    if (payload.chatbot_id) formData.append('chatbot_id', payload.chatbot_id);
    if (payload.edit_message_id) formData.append('edit_message_id', payload.edit_message_id);

    payload.files.forEach(file => {
      formData.append('files', file);
    });
    if (payload.drive_files?.length) {
      formData.append('drive_files', JSON.stringify(payload.drive_files.map((f) => f.file_id)));
    }

    // Use XHR for upload progress, then switch to streaming response
    const uploadUrl = `${apiUrl}/conversations/${conversationId}/stream-with-files`;
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${token}`,
      'X-Tenant-ID': tenantId || ''
    };

    // If we have progress callback, use XHR for upload portion
    let response: Response;
    if (options?.onUploadProgress) {
      response = await this.uploadWithProgress(
        uploadUrl,
        formData,
        headers,
        options.onUploadProgress,
        options.abortSignal
      );
    } else {
      response = await fetch(uploadUrl, {
        method: 'POST',
        headers,
        body: formData,
        signal: options?.abortSignal
      });
    }

    if (!response.ok) {
      // The files route refuses a file (type, size, count…) BEFORE it takes
      // the message, with the upload contract: thrown as an UploadError it is
      // worded like any refused file — in the user's language, naming it.
      const errorText = await response.text();
      throw fileRefusalFromBody(errorText) ?? apiErrorFromBody(response, errorText, 'Stream with files error');
    }

    if (!response.body) {
      throw new Error('No response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    try {
      // Same carry-over buffer as streamMessage — see the comment there.
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          logger.debug('Stream with files complete');
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? ''; // keep the trailing partial line

        for (const rawLine of lines) {
          const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
          if (!line.startsWith('data: ')) continue;

          try {
            const data = JSON.parse(line.slice(6));
            logger.debug('Stream event:', data.type);
            yield data as StreamEvent;

            // Exit only on done (the final event after ai_complete)
            if (data.type === 'done') {
              return;
            }
          } catch (error) {
            console.warn('[conversationService] Failed to parse SSE line:', line);
          }
        }
      }
    } catch (error) {
      console.error('[conversationService] Stream read error:', error);
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Network error. Please check your connection.');
      }
      throw error;
    } finally {
      try { reader.cancel(); } catch (_) { /* already closed */ }
    }
  }

  /**
   * Upload FormData using XHR to get upload progress
   * Returns a Response object compatible with fetch API
   */
  private uploadWithProgress(
    url: string,
    formData: FormData,
    headers: Record<string, string>,
    onProgress: (percent: number) => void,
    abortSignal?: AbortSignal
  ): Promise<Response> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Handle abort signal
      if (abortSignal) {
        abortSignal.addEventListener('abort', () => {
          xhr.abort();
          reject(new DOMException('Upload cancelled', 'AbortError'));
        });
      }

      // Track upload progress
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      };

      xhr.onload = () => {
        // Create a Response-like object from XHR
        const responseHeaders = new Headers();
        const headerString = xhr.getAllResponseHeaders();
        headerString.split('\r\n').forEach(line => {
          const [key, value] = line.split(': ');
          if (key && value) {
            responseHeaders.append(key, value);
          }
        });

        // For SSE, we need to handle the response body as a stream
        // Create a ReadableStream from the response
        const responseBlob = new Blob([xhr.response], { type: 'text/event-stream' });
        const stream = responseBlob.stream();

        const response = new Response(stream, {
          status: xhr.status,
          statusText: xhr.statusText,
          headers: responseHeaders
        });

        resolve(response);
      };

      xhr.onerror = () => {
        reject(new Error('Network error during upload'));
      };

      xhr.onabort = () => {
        reject(new DOMException('Upload cancelled', 'AbortError'));
      };

      // Open and configure request
      xhr.open('POST', url, true);
      xhr.responseType = 'blob'; // Get response as blob for streaming

      // Set headers
      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      // Send the request
      xhr.send(formData);
    });
  }

  /**
   * Send message with file attachments (synchronous)
   * Returns complete messages immediately (no streaming)
   */
  async sendMessageWithFiles(
    conversationId: string,
    payload: {
      content: string;
      files: File[];
      provider?: string;
      model?: string;
    }
  ): Promise<{ userMessage: Message; aiMessage: Message }> {
    logger.debug('Sending message with files:', {
      conversationId,
      fileCount: payload.files.length,
      contentLength: payload.content.length
    });

    const formData = new FormData();
    formData.append('message', payload.content);
    if (payload.provider) formData.append('provider', payload.provider);
    if (payload.model) formData.append('model', payload.model);

    payload.files.forEach(file => {
      formData.append('files', file);
    });

    const apiUrl = api.defaults.baseURL || (() => {
      throw new Error('API baseURL not configured. Check config service initialization.');
    })();
    const token = sessionStorage.getItem('access_token');
    const tenantId = sessionStorage.getItem('tenant_id');

    const response = await fetch(
      `${apiUrl}/conversations/${conversationId}/messages/with-files`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-ID': tenantId || ''
        },
        body: formData
      }
    );

    if (!response.ok) {
      // As on stream-with-files: a refused file (type, size, count…) is an
      // UploadError, anything else (auth, rate limit…) an ApiError.
      const errorText = await response.text();
      throw fileRefusalFromBody(errorText) ?? apiErrorFromBody(response, errorText, 'Upload failed');
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Upload failed');
    }

    logger.debug('Files uploaded successfully');

    // The message and its files are stored; the answer failed (ai_error says why).
    if (!data.ai_message) {
      throw new Error(data.ai_error || 'AI response generation failed. The file was uploaded but the AI could not respond.');
    }

    return {
      userMessage: this.transformMessage(data.user_message),
      aiMessage: this.transformMessage(data.ai_message)
    };
  }

  /**
   * Transform backend message format to frontend format
   */
  private transformMessage(backendMessage: BackendMessage): Message {
    return {
      message_id: backendMessage.message_id,
      conversation_id: backendMessage.conversation_id,
      role: backendMessage.sender_type,
      content: backendMessage.message_content,
      model_name: backendMessage.message_metadata?.model,
      mode_label: backendMessage.message_metadata?.mode_label,
      created_at: backendMessage.created_at,
      attachments: backendMessage.attachments || []
    };
  }
}

// Create and export singleton instance
const conversationService = new ConversationService();

export default conversationService;
